// AddPermitModal — manual permit creation for cases the auto-detector misses
// or for ad-hoc permits an operator wants to track.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';

const KIND_OPTIONS = [
  { v: 'nepa',                label: 'NEPA',                  bucket: 'environmental' },
  { v: 'section_404',         label: '§404 (Clean Water Act)', bucket: 'environmental' },
  { v: 'section_106',         label: '§106 (Historic)',       bucket: 'environmental' },
  { v: 'tribal_consultation', label: 'Tribal Consultation',   bucket: 'environmental' },
  { v: 'esa_section_7',       label: 'ESA §7',                bucket: 'environmental' },
  { v: 'floodplain',          label: 'Floodplain',            bucket: 'environmental' },
  { v: 'mbta',                label: 'Migratory Bird Treaty', bucket: 'environmental' },
  { v: 'railroad',            label: 'Railroad Crossing',     bucket: 'crossing' },
  { v: 'dot_access',          label: 'DOT Access',            bucket: 'crossing' },
  { v: 'highway',             label: 'Highway Crossing',      bucket: 'crossing' },
  { v: 'row_county',          label: 'County ROW',            bucket: 'row' },
  { v: 'row_city',            label: 'City ROW',              bucket: 'row' },
  { v: 'pole_attach',         label: 'Pole Attachment',       bucket: 'pole' },
  { v: 'make_ready',          label: 'Make-Ready',            bucket: 'pole' },
  { v: 'building',            label: 'Building Permit',       bucket: 'site' },
  { v: 'other',               label: 'Other',                 bucket: 'other' },
];

export default function AddPermitModal({
  projectId, onClose, onCreated,
}: {
  projectId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [kind, setKind] = useState('section_404');
  const [featureName, setFeatureName] = useState('');
  const [featureOwner, setFeatureOwner] = useState('');
  const [jurisdiction, setJurisdiction] = useState('federal');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => api.post('/permits', {
      project_id: projectId,
      kind,
      jurisdiction: jurisdiction || undefined,
      feature_name: featureName || undefined,
      feature_owner: featureOwner || undefined,
      notes: notes || undefined,
      project_only: true,   // v1 manual permits are project-wide; anchor-picking is a future feature
    }),
    onSuccess: () => onCreated(),
    onError: (e: any) => setError(e?.response?.data?.message ?? e?.message ?? 'Create failed'),
  });

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 540, maxWidth: '95%', background: '#fff', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}
      >
        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: '#fff' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Add permit manually</h2>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
            Use auto-detect for typical cases. Manual entry covers ad-hoc permits the detector misses.
          </div>
        </div>
        <div style={{ padding: 20, display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px 12px', alignItems: 'baseline' }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>Kind</label>
          <select value={kind} onChange={e => setKind(e.target.value)}
            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }}>
            {KIND_OPTIONS.map(k => <option key={k.v} value={k.v}>{k.label}</option>)}
          </select>

          <label style={{ fontSize: 12, color: '#64748b' }}>Jurisdiction</label>
          <select value={jurisdiction} onChange={e => setJurisdiction(e.target.value)}
            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }}>
            <option value="federal">Federal</option>
            <option value="state">State</option>
            <option value="county">County</option>
            <option value="city">City</option>
            <option value="tribal">Tribal</option>
            <option value="utility">Utility</option>
          </select>

          <label style={{ fontSize: 12, color: '#64748b' }}>Feature name</label>
          <input value={featureName} onChange={e => setFeatureName(e.target.value)}
            placeholder="e.g. Cache la Poudre River crossing"
            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />

          <label style={{ fontSize: 12, color: '#64748b' }}>Feature owner</label>
          <input value={featureOwner} onChange={e => setFeatureOwner(e.target.value)}
            placeholder="e.g. USACE, Xcel Energy, BNSF Railway"
            style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3 }} />

          <label style={{ fontSize: 12, color: '#64748b', alignSelf: 'start', paddingTop: 6 }}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            placeholder="Optional context"
            style={{ padding: '6px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 3, fontFamily: 'inherit', resize: 'vertical' }} />

          {error && (
            <div style={{ gridColumn: '1 / -1', padding: 8, background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: 4, fontSize: 11 }}>
              {error}
            </div>
          )}

          <div style={{ gridColumn: '1 / -1', padding: 8, background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', borderRadius: 4, fontSize: 11 }}>
            <b>Project-wide permit</b> — created without anchoring to a specific segment/pole/structure. To anchor to a feature, use Re-detect or click the feature on the as-built map (anchor-from-map is a future feature).
          </div>
        </div>

        <div style={{ padding: '12px 20px', background: '#f8fafc', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '7px 14px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, background: '#fff', cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => createMut.mutate()}
            disabled={createMut.isPending || !kind}
            style={{ padding: '7px 16px', fontSize: 12, fontWeight: 600, background: '#1e3a8a', color: '#fff', border: 0, borderRadius: 4, cursor: 'pointer' }}
          >
            {createMut.isPending ? 'Creating…' : 'Create permit'}
          </button>
        </div>
      </div>
    </div>
  );
}
